import { apiFetch, AUTH_EVENT, isRenewableSession, tokenExpiresSoon, refreshSession, hasPendingLogout, finishPendingLogout } from './utils/api';
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify'; // Asumiendo que usas react-toastify para notificaciones
import 'react-toastify/dist/ReactToastify.css';
import HomePage from './pages/HomePage';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Headercustom';
import config from './config';
import GameListComponent from './components/GameListComponent';
import RouteBoundary from './components/RouteBoundary';
import { useTranslation } from 'react-i18next';
import './App.css';
import { notificationUrl } from './utils/notificationUrl';
const CreateGameComponent = lazy(() => import('./components/CreateGameComponent'));
const Register = lazy(() => import('./components/Register'));
const Login = lazy(() => import('./components/Login'));
const EditProfileComponent = lazy(() => import('./components/EditProfileComponent'));
const GameRoute = lazy(() => import('./components/GameRoute'));
const HelpPage = lazy(() => import('./components/HelpPage'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const StatisticsComponent = lazy(() => import('./components/StatisticsComponent'));
const MapPreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./components/MapPreview')) : null;

function App() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(false);
  const [checkingSession, setCheckingSession] = useState(false);
  const [sessionAttempt, setSessionAttempt] = useState(0);
  const sessionGeneration = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const generation = ++sessionGeneration.current;
    let timeout;
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const isCurrent = () => !cancelled && generation === sessionGeneration.current && !!localStorage.getItem('token') &&
        localStorage.getItem('userId') === userId;

      if (token && userId) {
        setCheckingSession(true);
        let cachedUser = null;
        try {
          const cached = JSON.parse(localStorage.getItem('user') || 'null');
          if (cached?.userId === userId) cachedUser = cached;
        } catch { /* Ignore malformed or another account's cached profile. */ }
        if (cachedUser) { setUser(cachedUser); setLoading(false); }
        timeout = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await apiFetch(`${config.apiUrl}/api/users/${userId}`, {
            signal: controller.signal,
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const userData = await response.json();
            if (!isCurrent()) return;
            if (userData?.userId !== userId) throw new Error('Invalid profile response');
            setUser(userData);
            setSessionError(false);
            try { localStorage.setItem('user', JSON.stringify(userData)); } catch { /* The live session remains usable. */ }
          } else if (response.status === 401) {
            if (!isCurrent()) return;
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            localStorage.removeItem('user');
            setUser(null);
            setSessionError(false);
            setLoading(false);
            setCheckingSession(false);
          } else throw new Error('Profile temporarily unavailable');
        } catch (error) {
          if (isCurrent()) {
            setUser(previous => previous?.userId === userId ? previous : cachedUser);
            setSessionError(true);
          }
        } finally {
          clearTimeout(timeout);
          if (isCurrent()) { setLoading(false); setCheckingSession(false); }
        }
      } else {
        setUser(null);
        setSessionError(false);
        setLoading(false);
        setCheckingSession(false);
      }
    };

    fetchUser();
    return () => { cancelled = true; clearTimeout(timeout); controller.abort(); };
  }, [sessionAttempt]);

  useEffect(() => {
    const onAuth = () => {
      sessionGeneration.current++;
      setSessionError(false);
      setCheckingSession(false);
      setLoading(false);
      try {
        const cached = JSON.parse(localStorage.getItem('user') || 'null');
        setUser(localStorage.getItem('token') && cached?.userId === localStorage.getItem('userId') ? cached : null);
      } catch { setUser(null); }
    };
    const onStorage = event => { if (event.key === 'userId' || event.key === 'user' || event.key === null) onAuth(); };
    const renew = () => {
      if (hasPendingLogout()) { finishPendingLogout().catch(() => {}); return; }
      if (document.visibilityState === 'visible' && isRenewableSession() && tokenExpiresSoon()) {
        refreshSession().catch(() => {}); // Transient failures never discard a session or drafts.
      }
    };
    window.addEventListener(AUTH_EVENT, onAuth);
    window.addEventListener('storage', onStorage);
    window.addEventListener('online', renew);
    document.addEventListener('visibilitychange', renew);
    const timer = setInterval(renew, 60000);
    renew();
    return () => {
      clearInterval(timer);
      window.removeEventListener(AUTH_EVENT, onAuth);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('online', renew);
      document.removeEventListener('visibilitychange', renew);
    };
  }, []);

  useEffect(() => {
    if (!user?.userId || !('Notification' in window) || Notification.permission !== 'granted') return;
    let cancelled = false;
    let unsubscribe;
    const timer = setTimeout(() => {
      import('./firebase').then(firebase => {
        if (cancelled) return;
        unsubscribe = firebase.subscribeToMessages(payload => {
          if (document.visibilityState === 'visible') toast.info(payload.data.body, {
            onClick: () => { window.location.href = notificationUrl(payload.data.url); }
          });
        });
        firebase.refreshFCMToken();
      }).catch(error => console.warn('Notifications unavailable:', error.message));
    }, 1000);
    return () => { cancelled = true; clearTimeout(timer); unsubscribe?.(); };
  }, [user?.userId]);

  const updateUser = (userData) => {
    sessionGeneration.current++;
    setSessionError(false);
    setCheckingSession(false);
    setUser(previous => {
      const next = typeof userData === 'function' ? userData(previous) : userData;
      if (next) localStorage.setItem('user', JSON.stringify(next));
      else localStorage.removeItem('user');
      return next;
    });
  };

  if (loading) {
    return <p role="status">{t('loading')}</p>;
  }

  return (
    <Router>
      <div className="app">
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <Header user={user} setUser={updateUser} />
        <main className="app-main">
          <div className="container">
            {sessionError && <div className="session-warning" role="alert">
              <p>{t('sessionTemporarilyUnavailable')}</p>
              <button type="button" disabled={checkingSession} onClick={() => setSessionAttempt(value => value + 1)}>
                {t(checkingSession ? 'sessionChecking' : 'sessionRetry')}
              </button>
            </div>}
            <RouteBoundary><Suspense fallback={<p role="status">{t('loading')}</p>}><Routes>
              <Route path="/register" element={<Register setUser={updateUser} />} />
              <Route path="/login" element={<Login setUser={updateUser} />} />
              <Route path="/help" element={<HelpPage />} />
              {MapPreview && <Route path="/map-preview" element={<MapPreview />} />}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route element={<ProtectedRoute user={user} sessionUnavailable={sessionError} />}>
                <Route path="/" element={<HomePage user={user} />} />
                <Route path="/games" element={<GameListComponent showTabs={true} />} />
                <Route path="/create-game" element={<CreateGameComponent user={user} />} />
                <Route path="/statistics" element={<StatisticsComponent />} />
                <Route path="/edit-profile" element={<EditProfileComponent user={user} setUser={updateUser} />} />
                <Route path="/games/:id" element={<GameRoute />} />
              </Route>
            </Routes></Suspense></RouteBoundary>
          </div>
        </main>
        <footer className="app-footer">
          <div className="container">
            <p>&copy; {new Date().getFullYear()} JuanJo</p>
            <a href="/maps-privacy.html">{t('mapLegalNotice')}</a>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
